import {
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
  SpanConfig,
  Typography,
  useMediaQuery
} from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import clsx from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  CardCarousel as CardCarouselProps,
  SanityKeyed,
  UnifiedButton as UnifiedButtonProps
} from 'types/studio/studio';
import StarRatings from '../ProductDetailPage/StarRatings/StarRatings';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { CommonSectionProps } from '../types';
import { UnifiedButton } from '../UnifiedButton';
import * as styles from './CardCarousel.css';

const NUM_CARDS_TO_SHOW_CHEVRONS = 5;

const { colors } = vars;
const mediaColConfig = { xxs: 12, s: 6 } satisfies SpanConfig;

const Carousel = ({
  sectionType,
  carousel,
  isDarkMode = false,
  visibleOnPageLoad
}: {
  sectionType: string;
  carousel: CardCarouselProps['carousel'];
  isDarkMode?: boolean;
  visibleOnPageLoad: boolean;
}) => {
  const atSmall = useMediaQuery('s');
  const atMedium = useMediaQuery('m');
  const atLarge = useMediaQuery('l');
  const {
    scrollType = 'static',
    disableStaticLoop = false,
    autoscrollSpeed = 2,
    stopAutoscrollOnMouseEnter = false,
    carouselItems: carouselItemsOriginal = []
  } = carousel;

  const autoScrollConfig = AutoScroll({
    speed: autoscrollSpeed,
    startDelay: 0,
    stopOnInteraction: false,
    stopOnMouseEnter: stopAutoscrollOnMouseEnter
  });

  const shouldLoop = useMemo(
    () => (scrollType === 'static' ? !disableStaticLoop : true),
    [scrollType, disableStaticLoop]
  );

  // Use custom alignment value to offset carousel slides by section gutter spacing
  const carouselAlignValue = useMemo(() => {
    if (atLarge) return 120;
    if (atMedium) return 56;
    return 24;
  }, [atLarge, atMedium]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      slidesToScroll: 1,
      align: () => carouselAlignValue,
      skipSnaps: true,
      loop: shouldLoop
    },
    [...(scrollType === 'autoscroll' ? [autoScrollConfig] : []), WheelGesturesPlugin()]
  );

  useEffect(() => {
    // Reset focus to handle mobile touch
    const handleEmblaPointerUp = (api: EmblaCarouselType) => {
      api.rootNode().blur();
    };

    if (emblaApi) {
      // Reset carousel engine to consider new alignment
      emblaApi.reInit({
        align: () => carouselAlignValue
      });
      emblaApi.on('pointerUp', handleEmblaPointerUp);
    }

    return () => {
      emblaApi?.off('pointerUp', handleEmblaPointerUp);
    };
  }, [carouselAlignValue, emblaApi]);

  const carouselItems = useMemo(() => {
    // No need to duplicate if there are enough items or loop is disabled
    if (carouselItemsOriginal.length > 5 || !shouldLoop) return carouselItemsOriginal;

    // Duplicate all items to ensure the carousel can loop
    return [
      ...carouselItemsOriginal,
      ...carouselItemsOriginal.map((item) => ({ ...item, _key: `${item._key}-duplicate` }))
    ];
  }, [carouselItemsOriginal, shouldLoop]);

  if (!carouselItems?.length) return null;

  const showChevrons = carouselItemsOriginal.length >= NUM_CARDS_TO_SHOW_CHEVRONS;

  return (
    <div className={styles.carouselViewport} ref={emblaRef}>
      <div className={styles.carouselContainer}>
        {carouselItems.map((item, index, array) => {
          const { _key, title, backgroundImage, headline, bodyText, subtitle, showCTA, cardCta } =
            item;
          const isCtaCard = showCTA && !!cardCta;
          const isFirst = index === 0;
          const isLast = index === array.length - 1;
          let marginStyle = {};
          // Add additional padding to align non-looped edge items with section gutter
          if (!shouldLoop && isFirst) marginStyle = { marginLeft: carouselAlignValue };
          if (!shouldLoop && isLast) marginStyle = { marginRight: carouselAlignValue };

          return (
            <div key={_key} className={styles.carouselItem} style={marginStyle}>
              {backgroundImage?.image && (
                <StudioBaseImageV2
                  image={backgroundImage.image}
                  mobileImage={backgroundImage.mobileImage}
                  withMobileImage={backgroundImage.withMobileImage}
                  tabletImage={backgroundImage.tabletImage}
                  className={styles.carouselItemImage}
                  sizes={mediaColConfig}
                  eagerLoading={visibleOnPageLoad}
                />
              )}
              <div className={styles.carouselItemGradientOverlay} />
              {title && (
                <Typography variant="bodyMono" size="1" className={styles.carouselItemTitle}>
                  {title.toLocaleUpperCase()}
                </Typography>
              )}
              <div className={styles.carouselItemContent}>
                {headline && (
                  <Typography
                    variant="portalSubtitle"
                    size="1"
                    noPadding
                    className={styles.carouselItemHeadline}>
                    {headline}
                  </Typography>
                )}
                {bodyText &&
                  (atMedium ? (
                    <Typography
                      variant="body"
                      size="2"
                      noPadding
                      className={styles.carouselItemBodyText}>
                      {bodyText}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      size="1"
                      noPadding
                      className={styles.carouselItemBodyText}>
                      {bodyText}
                    </Typography>
                  ))}
                {subtitle && (
                  <Typography
                    variant="captionMono"
                    size={atSmall ? '1' : '2'}
                    noPadding
                    className={styles.carouselItemSubtitle}>
                    {subtitle.toLocaleUpperCase()}
                  </Typography>
                )}
                {isCtaCard && (
                  <UnifiedButton
                    size={atMedium ? 'medium' : 'small'}
                    sectionType={sectionType}
                    unifiedButton={cardCta}
                    className={styles.carouselItemCta}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {scrollType === 'static' && atMedium && showChevrons && (
        <div
          className={styles.carouselControlsContainer}
          style={{ stroke: isDarkMode ? colors.text.primaryInverse : colors.brand.primary }}>
          <IconButton
            ariaLabel="Previous Slide"
            onClick={() => emblaApi?.scrollPrev()}
            className={styles.carouselControlButton}>
            <ChevronLeftIcon size={48} color="inherit" className={styles.carouselControlIcon} />
          </IconButton>
          <IconButton
            ariaLabel="Next Slide"
            onClick={() => emblaApi?.scrollNext()}
            className={styles.carouselControlButton}>
            <ChevronRightIcon size={48} color="inherit" className={styles.carouselControlIcon} />
          </IconButton>
        </div>
      )}
    </div>
  );
};

const CardCarousel = (props: SanityKeyed<CardCarouselProps> & CommonSectionProps) => {
  const {
    _key: key,
    _type: sectionType,
    backgroundColor,
    theme,
    showHeaderSection,
    showRatingsSection,
    starRatings,
    headline,
    subtitle,
    showPrimaryHeaderCta,
    primaryHeaderCta,
    showSecondaryHeaderCta,
    secondaryHeaderCta,
    carousel,
    padding,
    visibleOnPageLoad
  } = props;

  const atMedium = useMediaQuery('m');
  const isDarkMode = theme === 'Dark';
  const backgroundColorValue = useMemo(() => {
    if (backgroundColor?.value) {
      return backgroundColor.value;
    }
    return isDarkMode ? colors.background.black : colors.background.white;
  }, [backgroundColor, isDarkMode]);

  const renderHeaderCta = useCallback(
    (cta: UnifiedButtonProps) => (
      <UnifiedButton
        size="medium"
        sectionType={sectionType}
        unifiedButton={cta}
        className={styles.headerCta}
      />
    ),
    [sectionType]
  );

  const primaryHeaderCtaContent = useMemo(
    () => showPrimaryHeaderCta && !!primaryHeaderCta && renderHeaderCta(primaryHeaderCta),
    [showPrimaryHeaderCta, primaryHeaderCta, renderHeaderCta]
  );
  const secondaryHeaderCtaContent = useMemo(
    () => showSecondaryHeaderCta && !!secondaryHeaderCta && renderHeaderCta(secondaryHeaderCta),
    [showSecondaryHeaderCta, secondaryHeaderCta, renderHeaderCta]
  );

  return (
    <section
      key={key}
      className={clsx(
        styles.container,
        styles.sectionPaddingTop[padding?.top || 'medium'],
        styles.sectionPaddingBottom[padding?.bottom || 'medium']
      )}
      style={{ backgroundColor: backgroundColorValue }}>
      {showHeaderSection && (
        <div className={styles.header}>
          <div className={clsx(styles.headerInfo, isDarkMode && styles.darkModeText)}>
            {showRatingsSection && starRatings && (
              <StarRatings
                _key={key}
                {...starRatings}
                darkMode={isDarkMode}
                disableNegativeBottomMargin
                noStarOutline
                visibleOnPageLoad={visibleOnPageLoad}
              />
            )}
            {headline && (
              <Typography
                variant="headlineSans"
                size="2"
                noPadding
                className={styles.headerHeadline}>
                {headline}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="subtitle" size="2" noPadding className={styles.headerSubtitle}>
                {subtitle}
              </Typography>
            )}
          </div>
          {atMedium && (
            <div className={styles.headerCtaContainer}>
              {primaryHeaderCtaContent}
              {secondaryHeaderCtaContent}
            </div>
          )}
        </div>
      )}

      {carousel && (
        <Carousel
          sectionType={sectionType}
          carousel={carousel}
          isDarkMode={isDarkMode}
          visibleOnPageLoad={visibleOnPageLoad}
        />
      )}

      {!atMedium && (
        <div className={styles.headerCtaContainer}>
          {primaryHeaderCtaContent}
          {secondaryHeaderCtaContent}
        </div>
      )}
    </section>
  );
};

export default CardCarousel;
